/**
 * interviewCommunicationQueries.ts
 *
 * React Query hooks for the Communication step's data needs:
 *  - ATS email templates (different categories per communication item)
 *  - User options for From / CC / BCC dropdowns
 *
 * The Communication step has 5 editor blocks. Each block will eventually
 * fetch templates from its own set of categories. For now most share the
 * same defaults — update the consts below when PM confirms per-block splits.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SelectOption } from '@kula-ai/components';
import {
  ATS_COMM_TEMPLATE_CHANNEL_EMAIL,
  fetchAtsCommTemplates,
  mapAtsTemplatesToSelectOptions,
  type AtsCommTemplate,
} from '@/api/atsCommTemplates';
import { useUsersWithScope } from '@/api/useUsersWithScope';

// Calendar-invite templates live on the `calendar_invite` channel in
// ATS, NOT `email`. Using the email constant for calendar scopes
// returns an empty list (that's the "no templates" bug).
const ATS_COMM_TEMPLATE_CHANNEL_CALENDAR_INVITE = 'calendar_invite' as const;

// ---------------------------------------------------------------------------
// Template categories per communication block
// TODO: update each const when PM confirms per-block category splits
// ---------------------------------------------------------------------------

/** Candidate calendar invite */
export const CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_interview_notice',
];

/** Candidate follow-up email */
export const CANDIDATE_EMAIL_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_interview_confirmation',
];

/** Interviewer calendar invite */
export const INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES: readonly string[] = [
  'interviewer_notice',
];

/** Interviewer follow-up email */
export const INTERVIEWER_EMAIL_TEMPLATE_CATEGORIES: readonly string[] = [
  'interviewer_interview_confirmation',
];

/** Self-schedule slot selection email */
export const SELF_SCHEDULE_EMAIL_TEMPLATE_CATEGORIES: readonly string[] = [
  // Final mapping is pending. Keep this focused to scheduling-related templates.
  'candidate_schedule_notice',
];

/** Candidate availability request email */
export const CANDIDATE_AVAILABILITY_EMAIL_TEMPLATE_CATEGORIES: readonly string[] =
  ['candidate_availability_notice'];

// ---------------------------------------------------------------------------
// Scope → categories mapping
// ---------------------------------------------------------------------------

/**
 * Scope values passed to `useInterviewEmailTemplateOptions`.
 * Currently the calendar invite and email for each audience share the same
 * scope — split into separate scopes if they need different categories.
 */
export type InterviewTemplateScope =
  | 'candidate-calendar'
  | 'candidate-email'
  | 'interviewer-calendar'
  | 'interviewer-email'
  | 'candidate'
  | 'interviewer'
  | 'self-schedule'
  | 'candidate-availability';

function getCategoriesForScope(
  scope: InterviewTemplateScope
): readonly string[] {
  switch (scope) {
    case 'candidate-calendar':
      return CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES;
    case 'candidate-email':
      return CANDIDATE_EMAIL_TEMPLATE_CATEGORIES;
    case 'interviewer-calendar':
      return INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES;
    case 'interviewer-email':
      return INTERVIEWER_EMAIL_TEMPLATE_CATEGORIES;
    case 'candidate':
      return [
        ...CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES,
        ...CANDIDATE_EMAIL_TEMPLATE_CATEGORIES,
      ];
    case 'interviewer':
      return [
        ...INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES,
        ...INTERVIEWER_EMAIL_TEMPLATE_CATEGORIES,
      ];
    case 'self-schedule':
      return SELF_SCHEDULE_EMAIL_TEMPLATE_CATEGORIES;
    case 'candidate-availability':
      return CANDIDATE_AVAILABILITY_EMAIL_TEMPLATE_CATEGORIES;
  }
}

function getChannelForScope(scope: InterviewTemplateScope): string {
  // Calendar-invite blocks (candidate-calendar / interviewer-calendar)
  // need templates on the `calendar_invite` channel. Email-body
  // blocks (candidate-email / interviewer-email / self-schedule) stay
  // on `email`. The mixed `candidate` / `interviewer` scopes fetch
  // email-channel templates; if someone starts using those scopes
  // again they should split per channel.
  switch (scope) {
    case 'candidate-calendar':
    case 'interviewer-calendar':
      return ATS_COMM_TEMPLATE_CHANNEL_CALENDAR_INVITE;
    default:
      return ATS_COMM_TEMPLATE_CHANNEL_EMAIL;
  }
}

// ---------------------------------------------------------------------------
// Template options hook
// ---------------------------------------------------------------------------

const templateQueryKeys = {
  all: ['interview-activity', 'communication', 'ats-comm-templates'] as const,
  byScope: (scope: InterviewTemplateScope) =>
    [...templateQueryKeys.all, scope] as const,
};

export type InterviewCommunicationTemplatesFormat = 'selectOptions' | 'none';

export function useInterviewEmailTemplateOptions(
  _scope: InterviewTemplateScope,
  _options?: { format?: 'selectOptions' }
): UseQueryResult<SelectOption[], Error>;

export function useInterviewEmailTemplateOptions(
  _scope: InterviewTemplateScope,
  _options: { format: 'none' }
): UseQueryResult<AtsCommTemplate[], Error>;

export function useInterviewEmailTemplateOptions(
  scope: InterviewTemplateScope,
  options?: { format?: InterviewCommunicationTemplatesFormat }
): UseQueryResult<SelectOption[] | AtsCommTemplate[], Error> {
  const categories = getCategoriesForScope(scope);
  const channel = getChannelForScope(scope);
  const format = options?.format ?? 'selectOptions';

  // `categories` is derived 1:1 from `scope`, already in the key via byScope.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    queryKey: [...templateQueryKeys.byScope(scope), channel, format],
    queryFn: () =>
      fetchAtsCommTemplates({
        channel,
        categories,
        items: 200,
        nameContains: '',
      }),
    staleTime: 60_000,
    select:
      format === 'none'
        ? undefined
        : (rows) => mapAtsTemplatesToSelectOptions(rows),
  }) as UseQueryResult<SelectOption[] | AtsCommTemplate[], Error>;
}

// ---------------------------------------------------------------------------
// Sender options — delegatable users only. CC/BCC uses `useCcBccUserOptions`
// instead; reusing this list there was ENG-3766.
// ---------------------------------------------------------------------------

export function useInterviewCommunicationSenderOptions(): UseQueryResult<
  SelectOption[],
  Error
> {
  return useUsersWithScope('email_delegatable');
}
